import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { LoginInputSchema } from '@starter/shared';
import { ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { PrimaryLogo } from '../components/public/PrimaryLogo.js';

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const from = (location.state as { from?: string } | null)?.from;

  useEffect(() => {
    document.title = 'Sign in — ScrubLedger';
  }, []);

  if (loading) {
    return (
      <div className="login-page login-page--centered">
        <div className="login-card login-card--loading">
          <div className="spinner" aria-hidden />
          <p>Checking session…</p>
        </div>
      </div>
    );
  }

  if (user) {
    const dest =
      from &&
      from !== '/login' &&
      from !== '/no-access' &&
      !from.startsWith('/request-demo')
        ? from
        : '/app';
    return <Navigate to={dest} replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const parsed = LoginInputSchema.safeParse({ email, password });
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]?.toString() ?? 'form';
        errs[key] = issue.message;
      }
      setFieldErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      await login(parsed.data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign in failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <aside className="login-page__brand-panel" aria-label="ScrubLedger">
        <p className="login-page__back">
          <Link to="/">← Back to ScrubLedger</Link>
        </p>
        <div className="login-page__brand-inner">
          <PrimaryLogo variant="png" displayWidth={220} loading="eager" className="login-page__logo-plate" />
          <p className="login-page__tagline">Less paperwork. More teaching.</p>
          <p className="login-page__brand-blurb">
            Clinical case tracking and document workflow for surgical technology programs.
          </p>
        </div>
      </aside>

      <div className="login-page__form-panel">
        <div className="login-card login-card--plain">
          <form className="login-form" onSubmit={handleSubmit} noValidate>
            <h1>Sign in to your program</h1>
            <p className="login-form__hint">
              Use the email and password provided by your program administrator.
            </p>

            {error && (
              <div className="banner banner--error" role="alert">
                {error}
              </div>
            )}

            <label className="field">
              <span className="field__label">Email</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                aria-invalid={!!fieldErrors.email}
              />
              {fieldErrors.email && <span className="field__error">{fieldErrors.email}</span>}
            </label>

            <label className="field">
              <span className="field__label">Password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                aria-invalid={!!fieldErrors.password}
              />
              {fieldErrors.password && <span className="field__error">{fieldErrors.password}</span>}
            </label>

            <button type="submit" className="btn btn--primary btn--block" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <footer className="login-card__footer">
            <span>Secure, session-based sign-in</span>
          </footer>
        </div>
      </div>
    </div>
  );
}
